import React, { useEffect, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import { VenueItem, Id, Url } from '@/types'
import Button, { ButtonProps } from '@components/Button'
import GridList from '@components/GridList'
import MediaCard, { MediaCardProps } from '@components/MediaCard'
import { Section } from '@components/Section'
import { SectionCTAProps } from '@components/Section/SectionCTA'

interface VenueFinderProps {
  title?: string
  abstract?: string
  contentType?: string
  cta?: Omit<SectionCTAProps, 'children'> & { label: string }
}

const VenueFinder: React.FC<VenueFinderProps> = ({ title, abstract, cta }) => {
  const [venues, setVenues] = useState<VenueItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const ITEMS_PER_PAGE = 4

  useEffect(() => {
    const fetchVenues = async () => {
      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          limit: ITEMS_PER_PAGE.toString(),
          page: '1',
        })

        const response = await fetch(
          `/api/visitmonaco/venues/list?${params.toString()}`,
        )
        const data = await response.json()
        setVenues(data.items || [])
      } catch (error) {
        console.error('Erreur lors de la récupération des venues', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchVenues()
  }, [])

  return (
    <Section>
      <Section.Content className={`gap-y-4`}>
        <Section.Title title={title} />
        <Section.Description
          description={abstract}
          className="max-w-3xl pb-10"
        />

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="h-72 bg-gray-200 animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : (
          <div className="mb-8 md:mb-10">
            <GridList
              mobileColumns={1}
              tabletColumns={2}
              desktopColumns={4}
              className="gap-x-4 gap-y-6"
            >
              {venues.map((venue) => (
                <MediaCard
                  key={venue.id}
                  tags={venue.type}
                  id={venue.id}
                  title={venue.title}
                  titleClassName="leading-6"
                  metadataClassName="text-grey-soft"
                  image={venue.image}
                  url={{ label: venue.url?.label, href: venue.url?.href }}
                  size="lg"
                  overlay={{
                    position: 'bottom',
                    overlayHeight: 'half',
                    overlayWidth: 'max',
                  }}
                  aspectRatio="aspect-[11/15]"
                />
              ))}
            </GridList>
          </div>
        )}

        {cta && cta.url && cta.label && (
          <div className="flex justify-center mt-8">
            <Button
              variant={cta.variant || 'primary'}
              href={cta.url.href}
              target={cta.url.target}
              icon={cta.iconName ? { name: cta.iconName } : { name: 'arrow' }}
              ariaLabel={cta.ariaLabel || cta.label}
              className={cta.className}
            >
              {cta.label}
            </Button>
          </div>
        )}
      </Section.Content>
    </Section>
  )
}

export default VenueFinder
